import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

// Explicitly destructuring necessary icons
const { FiX, FiCheck, FiAlertCircle, FiSettings, FiPlus, FiTrash2, FiChevronDown, FiZap } = FiIcons;

const RequestTestModal = ({ isOpen, onClose, profile, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Todos');
  const [categories, setCategories] = useState([{ name: 'Todos' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isManagingCategories, setIsManagingCategories] = useState(false);
  const [newCatName, setNewCatName] = useState('');

  useEffect(() => {
    if (profile?.id && isOpen) {
      fetchCategories();
    }
  }, [profile?.id, isOpen]);

  const fetchCategories = async () => {
    try {
      const { data, error: catError } = await supabase
        .from('app_categories')
        .select('*')
        .eq('user_id', profile.id);
      
      if (catError) throw catError;
      
      const cats = data || [];
      if (!cats.find(c => c.name === 'Todos')) {
        cats.unshift({ name: 'Todos', id: 'default-todos' });
      }
      setCategories(cats);
    } catch (err) {
      console.error("Erro ao buscar categorias:", err);
    }
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const { error: insError } = await supabase
        .from('app_categories')
        .insert({ user_id: profile.id, name: newCatName.trim() });
      
      if (insError) throw insError;
      setNewCatName('');
      fetchCategories();
    } catch (err) {
      alert("Erro ao adicionar categoria: " + err.message);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (id === 'default-todos') return;
    try {
      const { error: delError } = await supabase
        .from('app_categories')
        .delete()
        .eq('id', id);
      
      if (delError) throw delError;
      fetchCategories();
    } catch (err) {
      alert("Erro ao deletar categoria: " + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!profile?.id) return;

    if ((profile?.credits || 0) < 1) {
      setError('Saldo insuficiente para gerar um novo teste.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data, error: rpcError } = await supabase.rpc('claim_test_v1', {
        p_user_id: profile.id,
        p_title: title.trim(),
        p_category: category
      });

      if (rpcError) throw rpcError;
      
      if (data?.error) {
        throw new Error(data.error);
      }

      if (onSuccess) onSuccess();
      setTitle('');
      onClose();
    } catch (err) {
      setError(err.message || 'Erro ao gerar teste. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[140] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          onClick={() => !isManagingCategories && onClose()}
        />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-lg bg-white rounded-[40px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        >
          {isManagingCategories ? (
            <div className="p-8 md:p-10 overflow-y-auto">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h3 className="text-2xl font-black text-slate-900 uppercase italic">Categorias</h3>
                  <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">Organize seus testes</p>
                </div>
                <button 
                  onClick={() => setIsManagingCategories(false)} 
                  className="px-4 py-2 bg-slate-50 rounded-xl text-slate-400 font-black text-[10px] uppercase hover:bg-slate-100 transition-all"
                >
                  Voltar
                </button>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex space-x-2">
                  <input 
                    type="text" 
                    placeholder="Nome da Categoria" 
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    className="flex-1 p-4 bg-slate-50 rounded-2xl font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-none border border-transparent focus:bg-white transition-all"
                  />
                  <button 
                    onClick={handleAddCategory} 
                    className="p-4 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all"
                  >
                    <SafeIcon icon={FiPlus} />
                  </button>
                </div>

                <div className="space-y-2">
                  {categories.map(cat => (
                    <div key={cat.id || cat.name} className="flex justify-between items-center p-4 bg-slate-50 rounded-2xl group border border-transparent hover:border-slate-100 transition-all">
                      <span className="font-bold text-slate-700">{cat.name}</span>
                      {cat.name !== 'Todos' && (
                        <button 
                          onClick={() => handleDeleteCategory(cat.id)} 
                          className="p-2 text-slate-300 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-8 md:p-10 overflow-y-auto">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-black text-slate-900 uppercase italic leading-none mb-2">Novo Teste</h2>
                  <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest flex items-center gap-1">
                    Custo: <span className="text-indigo-600">1 Crédito</span>
                  </p>
                </div>
                <button 
                  onClick={onClose} 
                  className="p-4 rounded-2xl bg-slate-50 text-slate-400 hover:bg-slate-100 transition-all"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-2xl flex items-center gap-3 italic animate-in slide-in-from-top-2 duration-300">
                  <SafeIcon icon={FiAlertCircle} className="shrink-0" />
                  <span className="font-bold text-xs uppercase">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Assunto / Candidato</label>
                  <input 
                    type="text" 
                    required 
                    placeholder="Ex: Avaliação João Silva"
                    value={title} 
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full p-5 bg-slate-50 border border-transparent rounded-2xl font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all outline-none"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Categoria</label>
                    <button 
                      type="button" 
                      onClick={() => setIsManagingCategories(true)} 
                      className="text-[10px] font-black uppercase text-indigo-600 flex items-center gap-1 hover:underline"
                    >
                      <SafeIcon icon={FiSettings} className="text-xs" />
                      <span>Gerenciar</span>
                    </button>
                  </div>
                  <div className="relative group">
                    <select 
                      value={category} 
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full p-5 bg-slate-50 border border-transparent rounded-2xl font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all outline-none appearance-none"
                    >
                      {categories.map(cat => (
                        <option key={cat.id || cat.name} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                    <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 group-focus-within:text-indigo-600 transition-colors">
                      <SafeIcon icon={FiChevronDown} />
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-indigo-50 rounded-[30px] border border-indigo-100 flex items-start gap-4">
                  <div className="bg-indigo-600 p-2 rounded-xl mt-1 shrink-0">
                    <SafeIcon icon={FiCheck} className="text-white text-xs" />
                  </div>
                  <p className="text-[11px] font-bold text-indigo-900 leading-relaxed uppercase italic">
                    Cada teste tem o custo de 1 crédito. Você pode personalizar o nome e categoria para organização interna.
                  </p>
                </div>

                <button 
                  type="submit" 
                  disabled={loading || (profile?.credits || 0) < 1}
                  className="w-full py-5 bg-indigo-600 text-white rounded-3xl font-black uppercase italic shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:grayscale flex items-center justify-center gap-3"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <SafeIcon icon={FiZap} />
                      <span>Gerar Link de Teste</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default RequestTestModal;